import { PATHS } from "core/consts/pages";
import {
  hasAccessToAiChat,
  hasAccessToDTAApproval,
  hasAccessToDoctorDashboard,
  hasAccessToOnPremDebug,
  hasAccessToPatientDashboard,
  hasAccessToPredict,
  hasAccessToReport,
  hasAccessToUserManagement,
  isStaffAccount,
} from "core/model/accounts";
import { Account, AccountRole, ProductFeature } from "core/types";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import { ReactNode, useEffect, useMemo } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import {
  useAppDispatch,
  useConnecterWithCareseeker,
} from "reduxentities/hooks";
import { LOGGED_OUT } from "reduxentities/slices/authSlice";
import { useTranslations } from "translations";

const defaultRedirectPath = "/app/clinic/dashboard/todo/active";
const doctorDashboardRedirectPath = "/app/clinic/dashboard";
const dtaApproverPath = "/app/clinic/dashboard/rehab-forms-approval";
const onpremDebugRedirectPath = "/app/clinic/dashboard/onpremsetup/test";
export const predictRedirectPath = "/app/clinic/dashboard/predict";
const reportPath = "/app/clinic/dashboard/report";
const userManagementRedirectPath = "/app/clinic/dashboard/users";

function getPriorityRoute(
  account: Account | null | undefined,
  activeRoles: number[] | undefined,
  product_features: ProductFeature[] | undefined,
): string {
  if (!activeRoles || activeRoles.length === 0) {
    return "/logout?reason=unauthorized";
  }
  if (hasAccessToPatientDashboard(account, activeRoles)) {
    return defaultRedirectPath;
  }
  if (hasAccessToDoctorDashboard(activeRoles)) {
    return doctorDashboardRedirectPath;
  }
  if (hasAccessToUserManagement(account, activeRoles)) {
    return userManagementRedirectPath;
  }
  if (hasAccessToPredict(account, activeRoles, product_features)) {
    return predictRedirectPath;
  }
  if (hasAccessToReport(account, activeRoles)) {
    return reportPath;
  }
  if (hasAccessToOnPremDebug(account, activeRoles)) {
    return onpremDebugRedirectPath;
  }
  if (hasAccessToDTAApproval(account, activeRoles)) {
    return dtaApproverPath;
  }
  if (hasAccessToAiChat(product_features)) {
    return PATHS.APP.CHAT;
  }
  return "/logout?reason=unauthorized";
}

export function RequireRoles({
  children,
  requiredRoles,
}: {
  children?: ReactNode;
  requiredRoles?: AccountRole[];
}) {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { account, activeRoles, careseeker } = useConnecterWithCareseeker();
  const toast = useToast();
  const translations = useTranslations();

  const target = useMemo(
    () =>
      getPriorityRoute(
        account,
        activeRoles,
        careseeker?.product_features ?? undefined,
      ),
    [account, activeRoles, careseeker],
  );

  const requiredRolesSet = useMemo(
    () => Array.from(new Set([...(requiredRoles ?? [])])),
    [requiredRoles],
  );

  const isAllowed = useMemo(
    () =>
      isStaffAccount(account) ||
      requiredRolesSet.some((role) => activeRoles?.includes(role)),
    [account, requiredRolesSet, activeRoles],
  );

  useEffect(() => {
    if (!isAllowed && location.pathname === target) {
      dispatch(LOGGED_OUT());
    }
  }, [isAllowed, location.pathname, target, dispatch]);

  useEffect(() => {
    if (!isAllowed) {
      toast({
        color: "warning",
        message: translations.general.authorizationError,
      });
    }
  }, [isAllowed, translations, toast]);

  if (!isAllowed) {
    return <Navigate replace state={{ from: location }} to={target} />;
  }

  return <>{children ?? <Outlet />}</>;
}
